import { useEffect, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useVisit } from '@zenbusiness/application-commons-visit';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';

import Transition from '#client/components/Transition';
import { getWarmStartConfig, isValidWarmStartCode } from '#client/constants/warmStartMessages';
import { useVisitorThreadCreation } from '#client/hooks/useVisitorThreadCreation';

/**
 * ThreadCreator Component
 *
 * Handles the /guide/visitor route
 * Responsible for:
 * 1. Creating a new thread via API call for anonymous users
 * 2. Redirecting to /guide/visitor/thread/:threadUuid
 * 3. Handling errors during thread creation
 */

export default function VisitorThreadCreator() {
  const { visitUuid, visitProcessed } = useVisit();
  const [searchParams] = useSearchParams();
  const enableVeloForVisitors = useExperimentFlag('enable-velo-threads-for-visitors', false);

  // Add ref to track if thread creation has been initiated to prevent double execution
  const threadCreationInitiated = useRef(false);

  // Extract topic parameter from URL
  const topicCode = searchParams.get('topic');
  const isValidTopic = topicCode && isValidWarmStartCode(topicCode);

  // Redirect to dashboard if enableVeloForVisitors is false
  useEffect(() => {
    if (!enableVeloForVisitors) {
      window.location.replace('/');
    }
  }, [enableVeloForVisitors]);

  const {
    loading: threadLoading,
    error: threadError,
    retryCount,
    createNewThread,
    retryThreadCreation,
    canRetry,
    isNetworkError,
    isAuthError
  } = useVisitorThreadCreation();

  // Reset thread creation flag when visitUuid changes
  useEffect(() => {
    threadCreationInitiated.current = false;
  }, [visitUuid]);

  // Auto-create thread on mount (after entity loading is complete)
  useEffect(() => {
    // Create initial input object for thread creation
    const getInitialInput = () => {
      if (isValidTopic) {
        return getWarmStartConfig(topicCode);
      }
      return null; // Use default greeting
    };

    // Prevent double execution by checking if thread creation has already been initiated
    if (!threadLoading && !threadError && visitUuid && !threadCreationInitiated.current) {
      // Mark thread creation as initiated to prevent duplicate calls
      threadCreationInitiated.current = true;

      // Create thread with warm start input if available
      const initialInput = getInitialInput();
      createNewThread(visitUuid, initialInput);
    }
  }, [createNewThread, isValidTopic, threadError, threadLoading, topicCode, visitUuid]);

  if (!visitProcessed) {
    return <Transition type="VISIT_LOADING" />;
  }

  // Show thread creation loading state
  if (threadLoading) {
    return (
      <Transition
        type={retryCount > 0 ? 'THREAD_CREATING_RETRY' : 'THREAD_CREATING'}
        typeProps={{ retryCount }}
      />
    );
  }

  // Show error state with retry options
  if (threadError) {
    const getErrorType = () => {
      if (isAuthError) return 'THREAD_ERROR_AUTH';
      if (isNetworkError) return 'THREAD_ERROR_NETWORK';
      return 'THREAD_ERROR_GENERIC';
    };

    const getRetryButtonText = () => {
      if (retryCount > 0) {
        return `Retry (${retryCount}/3)`;
      }
      return 'Try Again';
    };

    const handleRetry = () => {
      // Create initial input for retry
      const initialInput = isValidTopic ? getWarmStartConfig(topicCode) : null;
      retryThreadCreation(visitUuid, initialInput);
    };

    return (
      <Transition
        actions={
          <Box alignItems="center" display="flex" flexDirection="column" gap={2} textAlign="center">
            {canRetry && (
              <Button color="primary" variant="contained" onClick={handleRetry}>
                {getRetryButtonText()}
              </Button>
            )}
          </Box>
        }
        type={getErrorType()}
      />
    );
  }

  // This should rarely be reached since useEffect will trigger thread creation
  // But it's here as a fallback
  return <Transition type="ALMOST_READY" />;
}
